import config from 'config';

import { waitForCookiebotConsent } from './cookiebotConsent';
import { loadScript } from './loadScript';

// SUB-329. Only for a build that ships no GTM web container. Staging ships its own
// since SUB-444 Phase 7 (`gtmWebContainer` in `config/staging.json`), so no
// environment sets `loadBrowserTag` today.
//
// Production's GA4 tag is owned by GTM (`GTM-KRV2W6KV`) and reports to the
// production property `G-3JJ41QQL5J`. Its host gate matches `staging-app…` too, so
// staging's browser hits land in the production property as well — while the
// staging server-side `sign_up` goes to the *test* property. The two halves of the
// chain therefore live in different properties, the ids never match a session in
// the target property, and stitching cannot be observed anywhere but production.
//
// Loading gtag for the test property here gives staging a browser session in the
// same property the staging emitter writes to, so capture → Measurement Protocol →
// session stitching becomes verifiable off production.
//
// Deliberately NOT routed through `stats.meinunterricht.de`: the server container's
// `GA4` tag forwards with a fixed measurement id, so a proxied hit would be
// rewritten into the production property. Staging thus reproduces capture → send,
// not the server-container hop.
export const loadGa4BrowserTag = async (): Promise<void> => {
	const { browserMeasurementId, loadBrowserTag } = config.ga4;
	if (!loadBrowserTag || !browserMeasurementId) return;

	// Only write GA4 cookies once the visitor has actually granted statistics
	// consent — the same gate `resolveCheckoutAnalyticsIds` reads at confirm time,
	// so the cookie exists exactly when capture expects to find it.
	await waitForCookiebotConsent();
	if (window.Cookiebot?.consent?.statistics !== true) return;

	loadScript(
		`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(browserMeasurementId)}`,
		{ async: 'true' }
	);

	window.dataLayer = window.dataLayer || [];
	// gtag.js reads its commands back off dataLayer as the raw `arguments` object;
	// pushing an array instead is not equivalent, so this stays the vendored shape.
	const gtag = function () {
		// biome-ignore lint/complexity/noArguments: vendored gtag shim - rest parameters would change what is pushed
		window.dataLayer.push(arguments);
	} as (...args: unknown[]) => void;

	gtag('js', new Date());
	gtag('config', browserMeasurementId);
};
